import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { decrypt, encrypt } from '../../../common/enc_dec';
import { financialReport_ } from '../../../slices/reportSlice';
import DataTable from 'react-data-table-component';
import moment from 'moment';
import Loader from '../../../components/loader';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import Swal from 'sweetalert2';
import { useModuleSettings } from '../../../components/moduleSettingsContext/ModuleSettingsContext';
import * as XLSX from 'xlsx';

const UsersEnrolled = () => {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { id, total_earnings } = location?.state || {};
    const queryParams = new URLSearchParams(location.search);
    const _id = queryParams.get("id") ? decrypt(queryParams.get("id")) : null;
    const courseId = id || _id;

    const { financialReport, loading } = useSelector((state) => state.report);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const { filter, setFilter, page, setPage, limit, setLimit } = useModuleSettings();
    const [isExporting, setIsExporting] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        if (courseId) {
            dispatch(financialReport_({
                course_id: courseId,
                period: filter,
                startDate,
                endDate,
                page,
                limit,
                search: searchTerm
            }));
        }
    }, [dispatch, courseId, filter, page, limit, startDate, endDate, searchTerm]);

    const handleViewUser = (row) => {
        navigate(`/users/view?id=${encrypt(`${row.user_id}`)}`, { state: { userData: row.enroll_user } });
    };

    const handleViewInvoice = (row) => {
        if (row.user_course_order?.invoice) {
            window.open(row.user_course_order.invoice, '_blank');
        } else {
            Swal.fire({
                icon: 'info',
                title: 'Invoice Not Available',
                text: 'Invoice is not available for this transaction.',
            });
        }
    };

    // Calculate financial summary
    const totalTransactions = financialReport?.paging?.total || 0;

    const columns = [
        {
            name: "Sr No",
            selector: (row, index) => (page - 1) * limit + index + 1,
            sortable: false,
            width: "80px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Transaction ID",
            selector: (row) => row.user_course_order?.razorpay_payment_id || "NA",
            sortable: true,
            minWidth: "200px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Order ID",
            selector: (row) => row.user_course_order?.order_id || "NA",
            sortable: true,
            minWidth: "180px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "User Name",
            selector: (row) => (
                <span className="text-capitalize">
                    {row.enroll_user?.full_name || "NA"}
                </span>
            ),
            sortable: true,
            minWidth: "200px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Email",
            selector: (row) => row.enroll_user?.email || "NA",
            sortable: true,
            minWidth: "250px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Phone Number",
            selector: (row) => (
                <>
                    {row.enroll_user?.country_code && row.enroll_user?.phone_number ?
                        `+${row.enroll_user.country_code} ${row.enroll_user.phone_number}` :
                        "NA"}
                </>
            ),
            minWidth: "180px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Amount",
            selector: (row) => (
                <span className="text-success fw-bold">
                    ₹{parseFloat(row.user_course_order?.amount || 0).toFixed(2)}
                </span>
            ),
            sortable: true,
            minWidth: "120px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Discount",
            selector: (row) => (
                <span className="text-warning">
                    ₹{parseFloat(row.user_course_order?.discount || 0).toFixed(2)}
                </span>
            ),
            sortable: true,
            minWidth: "120px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Payment Method",
            selector: (row) => (
                <span className="badge bg-info text-capitalize">
                    {row.user_course_order?.payment_method || "NA"}
                </span>
            ),
            sortable: true,
            minWidth: "150px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Purchase Date",
            selector: (row) => row.purchage_date ? moment(row.purchage_date).format("DD-MMM-YYYY HH:mm") : "NA",
            sortable: true,
            minWidth: "180px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Invoice",
            selector: (row) => (
                row.user_course_order?.invoice ?
                    <button 
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleViewInvoice(row)}
                    >
                        <i className="fe-file-text me-1"></i>
                        View
                    </button> :
                    <span className="badge bg-secondary">Not Available</span>
            ),
            minWidth: "120px",
            style: {
                fontSize: "14px",
            },
        },
        {
            name: "Actions",
            selector: (row) => (
                <div style={{ display: "flex", justifyContent: "center", gap: "8px" }}>
                    <button 
                        className="btn btn-sm btn-outline-info" 
                        onClick={() => handleViewUser(row)}
                        title="View User"
                    >
                        <i className="fe-eye"></i>
                    </button>
                </div>
            ),
            minWidth: "100px",
            style: {
                fontSize: "14px",
                display: "flex",
                justifyContent: "center",
            },
        },
    ];

    // Custom styles for DataTable
    const customStyles = {
        headCells: {
            style: {
                justifyContent: "center",
                backgroundColor: "#f8f9fa",
                fontWeight: "bold",
            },
        },
        cells: {
            style: {
                justifyContent: "center",
            },
        },
    };

    const handleFilterChange = (event) => {
        setFilter(event.target.value);
        setStartDate("");
        setEndDate("");
    };

    const handleCustomDateChange = () => {
        if (startDate && endDate && moment(startDate).isBefore(endDate)) {
            dispatch(
                financialReport_({
                    course_id: courseId,
                    period: "custom",
                    startDate: moment(startDate).format("YYYY-MM-DD"),
                    endDate: moment(endDate).format("YYYY-MM-DD"),
                    page,
                    limit,
                    search: searchTerm
                })
            );
        } else {
            Swal.fire({
                title: 'Invalid Dates',
                text: 'Please select valid start and end dates.',
                icon: 'error',
                confirmButtonText: 'OK'
            });
        }
    };

    const handleSearch = useCallback((event) => {
        const value = event.target.value;
        setSearchTerm(value);
        setPage(1); // Reset to first page when searching
        
        // Debounce the search to avoid too many API calls
        const timeoutId = setTimeout(() => {
            dispatch(financialReport_({
                course_id: courseId,
                period: filter,
                startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
                endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
                page: 1,
                limit,
                search: value
            }));
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [dispatch, courseId, filter, startDate, endDate, limit]);

    const handleExportData = useCallback(async () => {
        try {
            setIsExporting(true);

            // Fetch all data for export
            await dispatch(financialReport_({
                course_id: courseId,
                period: filter,
                startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
                endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
                page: 1,
                is_report: 1,
                search: searchTerm
            }))
                .then((response) => {
                    if (response.payload && response.payload.data.data) {
                        // Map the data to the format we want for Excel
                        const exportData = response.payload.data.data.map((row, index) => ({
                            "Sr No": index + 1,
                            "Transaction ID": row.user_course_order?.razorpay_payment_id || "NA",
                            "Order ID": row.user_course_order?.order_id || "NA",
                            "User Name": row.enroll_user?.full_name || "NA",
                            "Email": row.enroll_user?.email || "NA",
                            "Phone Number": row.enroll_user?.country_code && row.enroll_user?.phone_number
                                ? `+${row.enroll_user.country_code} ${row.enroll_user.phone_number}`
                                : "NA",
                            "Amount (₹)": parseFloat(row.user_course_order?.amount || 0).toFixed(2),
                            "Discount (₹)": parseFloat(row.user_course_order?.discount || 0).toFixed(2),
                            "Tax (₹)": parseFloat(row.user_course_order?.tax || 0).toFixed(2),
                            "Payment Method": row.user_course_order?.payment_method || "NA",
                            "Purchase Date": row.purchage_date
                                ? moment(row.purchage_date).format("DD-MMM-YYYY HH:mm:ss")
                                : "NA",
                            "Bank Transaction ID": row.user_course_order?.bank_transaction_id || "NA",
                            "UPI ID": row.user_course_order?.upi_id || "NA",
                            "Invoice Status": row.user_course_order?.invoice ? "Available" : "Not Available"
                        }));

                        // Create summary data
                        const summaryData = [
                            {
                                "Financial Report Summary": "Course Financial Overview",
                                "Value": ""
                            },
                            {
                                "Financial Report Summary": "Total Transactions",
                                "Value": response.payload.data.data.length
                            },
                            {
                                "Financial Report Summary": "Total Revenue (₹)",
                                "Value": response.payload.data.data.reduce((sum, item) => sum + parseFloat(item.user_course_order?.amount || 0), 0).toFixed(2)
                            },
                            {
                                "Financial Report Summary": "Total Discount (₹)",
                                "Value": response.payload.data.data.reduce((sum, item) => sum + parseFloat(item.user_course_order?.discount || 0), 0).toFixed(2)
                            },
                            // {
                            //     "Financial Report Summary": "Total Tax (₹)",
                            //     "Value": response.payload.data.data.reduce((sum, item) => sum + parseFloat(item.user_course_order?.tax || 0), 0).toFixed(2)
                            // },
                            {
                                "Financial Report Summary": "Export Date",
                                "Value": moment().format("DD-MMM-YYYY HH:mm:ss")
                            }
                        ];

                        // Create workbook and worksheets
                        const workbook = XLSX.utils.book_new();

                        // Add summary sheet
                        const summarySheet = XLSX.utils.json_to_sheet(summaryData);
                        XLSX.utils.book_append_sheet(workbook, summarySheet, "Financial Summary");

                        // Add transaction data sheet
                        const transactionSheet = XLSX.utils.json_to_sheet(exportData);
                        XLSX.utils.book_append_sheet(workbook, transactionSheet, "Transaction Data");

                        // Save the file
                        const fileName = `Financial_Report_${moment().format("YYYY-MM-DD")}.xlsx`;
                        XLSX.writeFile(workbook, fileName);

                        Swal.fire({
                            icon: "success",
                            title: "Export Successful",
                            text: `Data exported to ${fileName}`,
                        });

                        // Refresh current data
                        dispatch(financialReport_({ 
                            course_id: courseId, 
                            period: filter, 
                            startDate, 
                            endDate, 
                            page, 
                            limit, 
                            search: searchTerm 
                        }));
                    } else {
                        throw new Error("No data available for export");
                    }
                })
                .finally(() => {
                    setIsExporting(false);
                });
        } catch (error) {
            console.error("Export error:", error);
            Swal.fire({
                icon: "error",
                title: "Export Failed",
                text: error.message || "Unable to export financial data",
            });
            setIsExporting(false);
        }
    }, [dispatch, courseId, filter, startDate, endDate, searchTerm, page, limit]);

    if (loading) {
        return <Loader />;
    }

    return (
        <div className="">
            <div className="col-md-12 col-xl-12 mt-1">
                <div className="widget-rounded-circle card">
                    <div className="card-body">
                        {/* Header with Export Button */}
                        <div className="row mb-3">
                            <div className="col d-flex justify-content-end">
                                <button
                                    onClick={handleExportData}
                                    className="btn btn-outline-warning d-flex align-items-center gap-2"
                                    disabled={isExporting}
                                >
                                    {isExporting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                            Exporting...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fe-download me-1"></i>
                                            Export Report
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Filters and Search */}
                        <div className="row mb-3">
                            <div className="col-md-3">
                                <label className="form-label">Time Period</label>
                                <select className="form-select" onChange={handleFilterChange} value={filter}>
                                    <option value="">All</option>
                                    <option value="day">Today</option>
                                    <option value="week">Week</option>
                                    <option value="month">Month</option>
                                    <option value="year">Year</option>
                                    <option value="custom">Custom</option>
                                </select>
                            </div>
                            <div className="col-md-6">
                                {filter === "custom" && (
                                    <div className="row">
                                        <div className="col">
                                            <label className="form-label">Start Date</label>
                                            <DatePicker
                                                selected={startDate}
                                                onChange={(date) => setStartDate(date)}
                                                selectsStart
                                                startDate={startDate}
                                                endDate={endDate}
                                                placeholderText="Start Date"
                                                className="form-control"
                                                dateFormat="dd-MMM-yyyy"
                                            />
                                        </div>
                                        <div className="col">
                                            <label className="form-label">End Date</label>
                                            <DatePicker
                                                selected={endDate}
                                                onChange={(date) => setEndDate(date)}
                                                selectsEnd
                                                startDate={startDate}
                                                endDate={endDate}
                                                minDate={startDate}
                                                placeholderText="End Date"
                                                className="form-control"
                                                dateFormat="dd-MMM-yyyy"
                                            />
                                        </div>
                                        <div className="col-auto d-flex align-items-end">
                                            <button className="btn btn-primary" onClick={handleCustomDateChange}>
                                                Apply
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="col-md-3">
                                <label className="form-label">Search</label>
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Order ID, Transaction ID, Name, Email..."
                                        value={searchTerm}
                                        onChange={handleSearch}
                                    />
                                    <span className="input-group-text">
                                        <i className="fe-search"></i>
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Financial Summary Cards */}
                        <div className="row mb-4">
                            <div className="col-md-3">
                                <div className="card bg-primary text-white">
                                    <div className="card-body">
                                        <h6 className="card-title text-white">Total Transactions</h6>
                                        <h3 className="mt-2 text-white">{totalTransactions}</h3>
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-3">
                                <div className="card bg-success text-white">
                                    <div className="card-body">
                                        <h6 className="card-title text-white">Total Revenue</h6>
                                        <h3 className="mt-2 text-white">₹{total_earnings}</h3>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Data Table */}
                        <div className="row">
                            <div className="col-12">
                                <DataTable
                                    title={null}
                                    columns={columns}
                                    data={financialReport?.data || []}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={financialReport?.paging?.total}
                                    paginationDefaultPage={page}
                                    paginationPerPage={limit}
                                    paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
                                    onChangeRowsPerPage={(newrow) => setLimit(newrow)}
                                    onChangePage={(newPage) => setPage(newPage)}
                                    responsive
                                    selectableRowsHighlight
                                    highlightOnHover
                                    striped
                                    fixedHeader
                                    persistTableHead
                                    noDataComponent={
                                        <div className="text-center p-4">
                                            <h5 className="text-muted">No Financial Data Found</h5>
                                            <p className="text-muted">No transactions available for the selected criteria.</p>
                                        </div>
                                    }
                                    customStyles={customStyles}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UsersEnrolled;